import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import Icon from '@/components/Icon';
import SemanticBlock from '@/components/Landing/SemanticBlock';
import { useLanding } from '@/components/Landing/LandingKit';
import { Card, SectionIntro } from '@/components/Landing/MarketingKit';
import Text from '@/components/Typography/Text';
import { openUseCaseDraft } from '@/lib/openUseCaseDraft';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import { eventName, MARKETING_ACTION, trackMarketingEvent } from '@/constants/utils/analyticsEvents';

const EMPTY = [];
const labelFor = value => String(value || '').replace(/[-_]/g, ' ').replace(/^./, char => char.toUpperCase());

function Action({ children, onPress, selected, primary, label, expanded }) {
  const { c } = useLanding();
  return <Pressable onPress={onPress} accessibilityRole="button" accessibilityLabel={label}
    accessibilityState={{ ...(selected == null || Platform.OS === 'web' ? {} : { selected }), ...(expanded == null ? {} : { expanded }) }}
    {...(Platform.OS === 'web' ? { ...(selected != null ? { 'aria-pressed': selected } : {}), ...(expanded != null ? { 'aria-expanded': expanded } : {}) } : {})}
    style={({ hovered, pressed }) => ({ minHeight: 44, paddingHorizontal: 16, paddingVertical: 12,
      borderRadius: 10, borderWidth: 1, borderColor: primary || selected ? c.ink : c.ruleStrong,
      backgroundColor: primary || selected ? c.ink : hovered || pressed ? c.panel : c.cardSolid,
      justifyContent: 'center', alignItems: 'center', maxWidth: '100%' })}>
    <Text weight="medium" style={{ color: primary || selected ? c.page : c.ink, fontSize: 14, lineHeight: 20 }}>{children}</Text>
  </Pressable>;
}

function DetailList({ title, items }) {
  const { c } = useLanding();
  if (!items.length) return null;
  return <View style={{ gap: 8 }}>
    <Text weight="semibold" style={{ color: c.ink, fontSize: 14 }}>{title}</Text>
    {items.map((item, index) => <View key={`${index}-${item}`} style={{ flexDirection: 'row', gap: 8 }}>
      <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22 }}>•</Text>
      <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22, flex: 1, minWidth: 0 }}>{item}</Text>
    </View>)}
  </View>;
}

export function UseCasePromptCard({ item, preserveThroughSignIn = false }) {
  const { c, isXS } = useLanding();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const mode = item.mode === 'work' ? 'Work' : 'Chat';
  const inputs = item.inputs?.length ? item.inputs : (item.attachments || []).map(file => file.label).filter(Boolean);
  const checks = (item.nextSteps || []).filter(Boolean);
  const openDraft = () => {
    const slug = Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.location.pathname.match(/^\/use-cases\/([a-z0-9-]+)$/)?.[1] : null;
    if (slug) trackMarketingEvent(MARKETING_ACTION.CTA_CLICKED,
      eventName('use-cases', slug, `${mode.toLowerCase()}_draft`, 'app'));
    return preserveThroughSignIn ? openUseCaseDraft(router, item, { preserveThroughSignIn: true })
      : openUseCaseDraft(router, item);
  };
  const copy = async () => {
    try {
      const copied = await Clipboard.setStringAsync(item.prompt);
      setCopyStatus(copied === false ? 'Could not copy. Expand the prompt to select its text.' : 'Prompt copied.');
    } catch (_) { setCopyStatus('Could not copy. Expand the prompt to select its text.'); }
  };
  return <SemanticBlock tag="article" aria-label={item.title}>
    <Card padded={false} style={{ overflow: 'hidden' }}>
      <View style={{ flexDirection: isXS ? 'column' : 'row', alignItems: 'stretch' }}>
        <View style={{ flex: isXS ? undefined : 1.2, minWidth: 0, padding: isXS ? 20 : 28, gap: 16 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}><Icon name={mode === 'Work' ? 'file-lines' : 'message'} size={16} color={c.muted} /><Text weight="medium" style={{ color: c.muted, fontSize: 13, lineHeight: 20 }}>{mode}{item.category ? ` · ${labelFor(item.category)}` : ''}</Text></View>
          <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: isXS ? 22 : 26, lineHeight: isXS ? 29 : 33, letterSpacing: -0.5 }}>{item.title}</Text>
          <Text tag="p" style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>{item.outcome || 'A draft response to this prompt. Compare it with your sources before using it.'}</Text>
          <Action expanded={expanded} label={`${expanded ? 'Hide' : 'Read'} example prompt: ${item.title}`} onPress={() => setExpanded(value => !value)}>{expanded ? 'Close workflow' : 'Explore workflow'}</Action>
          {expanded && <View style={{ gap: 18 }}>
            {!!item.modeReason && <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 23 }}>{item.modeReason}</Text>}
            {!!item.artifact && <Text weight="medium" style={{ color: c.ink, fontSize: 14 }}>Deliverable: {item.artifact}</Text>}
            <DetailList title="What to provide" items={inputs.length ? inputs : ['Your task, source material and constraints']} />
            <Text selectable tag="p" style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24 }}>{item.prompt}</Text>
            <DetailList title="What to check" items={checks.length ? checks : ['Verify names, figures and source references', 'Review the draft before using it']} />
          </View>}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            <Action primary label={`Open ${mode} draft: ${item.title}`} onPress={openDraft}>Open {mode} draft</Action>
            <Action label={`Copy prompt: ${item.title}`} onPress={copy}>Copy prompt</Action>
          </View>
          {!!copyStatus && <Text accessibilityRole={safeAccessibilityRole('status')} accessibilityLiveRegion="polite" style={{ color: c.muted, fontSize: 13, lineHeight: 20 }}>{copyStatus}</Text>}
          {mode === 'Work' && <Text style={{ color: c.muted, fontSize: 13, lineHeight: 20 }}>Requires Plus and uses credits. Review estimates when offered for paid steps; total cost depends on the work.</Text>}
        </View>

      </View>
    </Card>
  </SemanticBlock>;
}

export default function UseCaseExamplePrompts({ useCaseName, examplePrompts = EMPTY, promptCategories = EMPTY, intro }) {
  const { c, pad, space, isXS } = useLanding();
  const [mode, setMode] = useState('all');
  const [category, setCategory] = useState('all');
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setMode('all'); setCategory('all'); setShowAll(false); }, [useCaseName]);
  const prompts = useMemo(() => (Array.isArray(examplePrompts) ? examplePrompts : EMPTY)
    .filter(item => item?.title && item?.prompt).slice().sort((a, b) => (a.order || 0) - (b.order || 0)), [examplePrompts]);
  const inMode = useMemo(() => prompts.filter(item => mode === 'all' || (item.mode || 'chat') === mode), [prompts, mode]);
  const categories = useMemo(() => {
    const used = new Set(inMode.map(item => item.category).filter(Boolean));
    const labels = new Map((Array.isArray(promptCategories) ? promptCategories : EMPTY).map(item => [item.value, item.label]));
    return [...used].map(value => ({ value, label: labels.get(value) || labelFor(value) }));
  }, [inMode, promptCategories]);
  const filtered = inMode.filter(item => category === 'all' || item.category === category);
  if (!prompts.length) return null;
  const chooseMode = value => { setMode(value); setCategory('all'); setShowAll(false); };
  const chooseCategory = value => { setCategory(value); setShowAll(false); };
  return <SemanticBlock tag="section" aria-label={`Workflows for ${useCaseName || 'you'}`} style={{ width: '100%', alignItems: 'center', paddingLeft: pad, paddingRight: pad, paddingTop: space, paddingBottom: space }}>
    <View style={{ width: '100%', maxWidth: 1100 }}>
      <SectionIntro kicker="Practical workflows" lines={['Choose a task. See the result.']} lede={intro || `Explore practical tasks for ${useCaseName || 'your work'}.`} />
      <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 22, marginTop: 20, maxWidth: 760 }}>These are example prompts, not recorded model responses. Opening a draft adds the prompt; supply your own sources before sending.</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 28 }}>
        {[['all', 'All workflows'], ['chat', 'Chat'], ['work', 'Work']].filter(([value]) => value === 'all' || prompts.some(item => (item.mode || 'chat') === value)).map(([value, label]) => <Action key={value} selected={mode === value} onPress={() => chooseMode(value)} label={value === 'all' ? 'Show all workflows' : `Show ${label} workflows`}>{label}</Action>)}
      </View>
      {categories.length > 1 && <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        <Action selected={category === 'all'} onPress={() => chooseCategory('all')} label="Show all task categories">All categories</Action>
        {categories.map(item => <Action key={item.value} selected={category === item.value} onPress={() => chooseCategory(item.value)} label={`Show ${item.label} examples`}>{item.label}</Action>)}
      </View>}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 24 }}>{(showAll ? filtered : filtered.slice(0, 6)).map(item => <View key={`${useCaseName}-${item.order}-${item.title}`} style={{ width: isXS ? '100%' : '48.8%', minWidth: 0 }}><UseCasePromptCard item={item} /></View>)}</View>
      {filtered.length > 6 && <View style={{ alignItems: 'flex-start', marginTop: 24 }}><Action expanded={showAll} onPress={() => setShowAll(value => !value)}>{showAll ? 'Show fewer examples' : `Show ${filtered.length - 6} more examples`}</Action></View>}
    </View>
  </SemanticBlock>;
}
